/**
 * SmartRow (Nano-Metadata Table)
 *
 * ✅ ENTERPRISE V2: Single Row Display
 * Uses deterministic data from useNanoMetadataTableSync
 */

import React, { useState } from 'react';

import { FileText } from 'lucide-react';

import DNAColumn from '@/components/smart-notebook/SmartTable/DNAColumn';
import FileIcon from '@/components/smart-notebook/SmartTable/FileIcon';
import HealthBar from '@/components/smart-notebook/SmartTable/HealthBar';
import StatusBadge from '@/components/smart-notebook/SmartTable/StatusBadge';
import { getFileTypeTooltip } from '@/pages/intelligence-studio/utils/fileUtils';
import '@/components/smart-notebook/SmartTable/SmartTable.css';

// ✅ FileTypeIcon component (same as SourcesPanel)
const FileTypeIcon = ({ extension, className = 'h-5 w-5' }) => {
  const [imageError, setImageError] = useState(false);
  const ext = (extension || '').toLowerCase();
  const tooltip = getFileTypeTooltip(ext);

  const VSCODE_ICONS_BASE =
    'https://raw.githubusercontent.com/vscode-icons/vscode-icons/master/icons';

  // VS Code Icons for Office documents
  const cdnIcons = {
    pdf: `${VSCODE_ICONS_BASE}/file_type_pdf.svg`,
    doc: `${VSCODE_ICONS_BASE}/file_type_word.svg`,
    docx: `${VSCODE_ICONS_BASE}/file_type_word.svg`,
    xls: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
    xlsx: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
    csv: `${VSCODE_ICONS_BASE}/file_type_excel.svg`,
    ppt: `${VSCODE_ICONS_BASE}/file_type_powerpoint.svg`,
    pptx: `${VSCODE_ICONS_BASE}/file_type_powerpoint.svg`,
  };

  const cdnIcon = cdnIcons[ext];

  // If we have a CDN icon and it hasn't failed, use it
  if (cdnIcon && !imageError) {
    return (
      <img
        src={cdnIcon}
        alt={`${ext} file`}
        title={tooltip}
        className={className}
        style={{ objectFit: 'contain', cursor: 'help' }}
        onError={() => setImageError(true)}
      />
    );
  }

  // Fallback to colored badges for non-CDN icons or if CDN fails
  // PDF - Red accent
  if (ext === 'pdf') {
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400`}
        title={tooltip}
        style={{ cursor: 'help' }}
      >
        <span className="text-[9px] font-bold">PDF</span>
      </div>
    );
  }

  // Word - Blue accent
  if (ext === 'doc' || ext === 'docx') {
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400`}
        title={tooltip}
        style={{ cursor: 'help' }}
      >
        <span className="text-[9px] font-bold">DOC</span>
      </div>
    );
  }

  // Excel - Green accent
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') {
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400`}
        title={tooltip}
        style={{ cursor: 'help' }}
      >
        <span className="text-[9px] font-bold">XLS</span>
      </div>
    );
  }

  // PowerPoint - Orange accent
  if (ext === 'ppt' || ext === 'pptx') {
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400`}
        title={tooltip}
        style={{ cursor: 'help' }}
      >
        <span className="text-[9px] font-bold">PPT</span>
      </div>
    );
  }

  // Image files - Amber accent
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff'].includes(ext)) {
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400`}
        title={tooltip}
        style={{ cursor: 'help' }}
      >
        <span className="text-[9px] font-bold">IMG</span>
      </div>
    );
  }

  // Text files - Gray accent
  if (ext === 'txt' || ext === 'md' || ext === 'rtf') {
    return (
      <div
        className={`${className} flex items-center justify-center rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400`}
        title={tooltip}
        style={{ cursor: 'help' }}
      >
        <span className="text-[9px] font-bold">TXT</span>
      </div>
    );
  }

  // Default - Generic file
  return (
    <div
      className={`${className} flex items-center justify-center rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400`}
      title={tooltip}
      style={{ cursor: 'help' }}
    >
      <FileText className="h-3 w-3" />
    </div>
  );
};

export default function SmartRow({ row, onClick, isSelected, onRemove, onRetry, compact }) {
  const fileName = row?.filename || row?.basic_info?.name || 'Unknown';
  const fileSize = row?.basic_info?.size_formatted || '';
  const baseType = row?.basic_info?.base_type || 'document';
  const extension = row?.basic_info?.extension || '';

  const isLoading = row?.isLoading === true;
  const hasError = row?.hasError === true;
  const isReady = row?.isReady === true;

  // User-friendly insight based on status and health
  const idxScore = row?.quick_health?.indexCoveragePct ?? row?.quick_health?.ai_readiness_score;
  const txtScore = row?.quick_health?.textHealthPct ?? null;
  const healthTier = row?.quick_health?.health_tier || 'pending';

  const getInsightText = () => {
    if (isLoading) return 'Analyzing document...';
    if (hasError) return 'Processing failed - click to retry';
    if (idxScore === null || idxScore === undefined) return 'Ready for AI analysis';

    // Prefer TXT signal when present (matches user-visible extracted text)
    if (typeof txtScore === 'number' && txtScore < 70) {
      return 'Indexed, but low text quality - Retry OCR recommended';
    }

    if (healthTier === 'excellent' || idxScore >= 95) return 'Fully indexed - ready for AI';
    if (healthTier === 'good' || idxScore >= 85) return 'Indexed - ready for AI';
    if (healthTier === 'fair' || idxScore >= 70)
      return 'Partially indexed - AI may miss some content';
    if (healthTier === 'poor' || idxScore >= 50)
      return 'Low indexing coverage - consider reprocessing';
    return 'Very low indexing coverage - re-upload recommended';
  };

  const insight = row?.user_display?.insight || row?.details?.micro_insight || getInsightText();

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER ROW
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div
      className={`smart-row ${isSelected ? 'smart-row-selected' : ''} ${isLoading ? 'smart-row-loading' : ''} ${hasError ? 'smart-row-error' : ''} ${compact ? 'smart-row-compact' : ''}`}
      onClick={onClick}
      role="row"
    >
      {/* DOCUMENT COLUMN - Using proper FileIcon component */}
      <div className="smart-cell smart-cell-document">
        <div className="document-cell">
          <FileIcon ext={extension} className="document-icon-img" />
          <div className="document-info">
            <div className="document-name" title={fileName}>
              {fileName}
            </div>
            <div className="document-meta">
              {fileSize && <span>{fileSize}</span>}
              {extension && <span className="document-type">• {extension.toUpperCase()}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* DNA COLUMN */}
      <div className="smart-cell smart-cell-dna">
        <DNAColumn row={row} />
      </div>

      {/* STATUS COLUMN */}
      <div className="smart-cell smart-cell-status">
        <StatusBadge row={row} />
      </div>

      {/* HEALTH COLUMN */}
      <div className="smart-cell smart-cell-health">
        <HealthBar row={row} />
      </div>

      {/* INSTANT INSIGHT COLUMN */}
      <div className="smart-cell smart-cell-insight">
        <div className="insight-cell">
          <span
            className="insight-badge"
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            {isLoading ? (
              '⏳'
            ) : hasError ? (
              '❌'
            ) : (
              <FileTypeIcon extension={extension} className="h-4 w-4" />
            )}
            {!isLoading && !hasError && (
              <span>{baseType.charAt(0).toUpperCase() + baseType.slice(1)}</span>
            )}
          </span>
          <span className="insight-text" title={insight}>
            {insight ||
              (isLoading ? 'Processing...' : isReady ? 'Ready for AI analysis' : 'Waiting...')}
          </span>
        </div>
      </div>
    </div>
  );
}
